import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useAudio } from '../contexts/AudioContext';

const SongCard = ({ song, onDelete }) => {
  // Format duration if available
  const duration = song.duration || '0:00';
  const [albumTitle, setAlbumTitle] = useState(song.albumTitle || null);
  const [thumbnailUrl, setThumbnailUrl] = useState('');
  const { currentTrack, isPlaying, playTrack } = useAudio();
  
  // Check if this song is currently playing
  const isThisSongPlaying = currentTrack && currentTrack.id === song.id && isPlaying;
  
  // Fetch pre-signed URL for thumbnail if available
  useEffect(() => {
    const fetchThumbnailUrl = async () => {
      if (song.thumbnailKey) {
        try {
          console.log('Fetching pre-signed URL for thumbnail with key:', song.thumbnailKey);
          const response = await axios.get(`${config.API_URL}/get-presigned-url?key=${encodeURIComponent(song.thumbnailKey)}`);
          if (response.data && response.data.presignedUrl) {
            console.log('Successfully fetched pre-signed URL for thumbnail');
            setThumbnailUrl(response.data.presignedUrl);
          } else {
            console.error('No presigned URL in response for thumbnail');
            // Fallback to direct URL if available
            if (song.thumbnailUrl) {
              console.log('Falling back to direct thumbnailUrl');
              setThumbnailUrl(song.thumbnailUrl);
            }
          }
        } catch (error) {
          console.error('Error fetching pre-signed URL for thumbnail:', error);
          // Fallback to direct URL if available
          if (song.thumbnailUrl) {
            console.log('Error occurred, falling back to direct thumbnailUrl');
            setThumbnailUrl(song.thumbnailUrl);
          }
        }
      } else if (song.thumbnailUrl) {
        // If we have a URL but no key, use the URL directly
        console.log('No thumbnailKey available, using direct thumbnailUrl');
        setThumbnailUrl(song.thumbnailUrl);
      } else {
        console.log('No thumbnail information available for song:', song.id, song.title);
      }
    };
    
    fetchThumbnailUrl();
  }, [song.thumbnailKey, song.thumbnailUrl, song.id, song.title]);
  


  useEffect(() => {
    // If we have an albumId but no albumTitle, fetch the album details
    if (song.albumId && !albumTitle) {
      const fetchAlbumTitle = async () => {
        try {
          const response = await axios.get(`${config.API_URL}/albums/${song.albumId}`);
          if (response.data && response.data.title) {
            setAlbumTitle(response.data.title);
          } else {
            // Album not found - it might have been deleted
            setAlbumTitle('Album not found');
          }
        } catch (error) {
          console.error('Error fetching album title:', error);
          // Handle 404 errors specifically - album was deleted
          if (error.response && error.response.status === 404) {
            setAlbumTitle('Album not found');
          }
        }
      };
      
      fetchAlbumTitle();
    }
  }, [song.albumId, albumTitle]);
  
  const handleDelete = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (window.confirm(`Are you sure you want to delete "${song.title}"?`)) {
      try {
        await axios.delete(`${config.API_URL}/songs/${song.id}`);
        if (onDelete) onDelete(song.id);
      } catch (error) {
        console.error('Error deleting song:', error);
        alert('Failed to delete song. Please try again.');
      }
    }
  };
  
  // Handle play button click
  const handlePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Prepare track data for the audio context
    if (song.audioFiles && song.audioFiles.length > 0) {
      // Find the default audio file or use the first one
      const defaultAudio = song.audioFiles.find(file => file.isDefault) || song.audioFiles[0];
      
      if (defaultAudio) {
        // Create a track object with all necessary information
        const track = {
          id: song.id,
          title: song.title,
          albumTitle: albumTitle,
          thumbnailUrl: thumbnailUrl,
          fileKey: defaultAudio.fileKey,
          duration: defaultAudio.duration || song.duration
        };
        
        // Play the track using the audio context
        playTrack(track);
      }
    }
  };

  return (
    <Link 
      to={`/songs/${song.id}`}
      className="block bg-white hover:bg-gray-50 border-b border-gray-200 transition-colors duration-200"
    >
      <div className="py-3 px-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            {thumbnailUrl ? (
              <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center mr-3 overflow-hidden relative group">
                <img 
                  src={thumbnailUrl} 
                  alt={song.title} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    console.error(`Failed to load thumbnail image for song: ${song.id} - ${song.title}`);
                    // If image fails to load, show the default icon
                    e.target.style.display = 'none';
                    e.target.parentNode.innerHTML = `
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                      </svg>
                    `;
                  }}
                />
                {song.audioFiles && song.audioFiles.length > 0 && (
                  <button 
                    onClick={handlePlay}
                    className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    aria-label={isThisSongPlaying ? 'Pause' : 'Play'}
                  >
                    {isThisSongPlaying ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM7 8a1 1 0 012 0v4a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v4a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
            ) : (
              <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center mr-3 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
              </div>
            )}
            <div>
              <h3 className="text-sm font-medium text-gray-900">{song.title}</h3>
              <div className="flex items-center text-xs text-gray-500 mt-0.5">
                {song.albumId && albumTitle && albumTitle !== 'Album not found' && (
                  <Link to={`/albums/${song.albumId}`} className="album-title hover:text-indigo-600 transition-colors" data-album-id={song.albumId}>
                    {albumTitle}
                  </Link>
                )}
                {song.albumId && albumTitle === 'Album not found' && (
                  <span className="text-amber-600">Album not found</span>
                )}
                {song.albumId && !albumTitle && (
                  <span>Loading album...</span>
                )}
                {!song.albumId && <span>No album</span>}
                {song.duration && (
                  <>
                    <span className="mx-1">•</span>
                    <span>{duration}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleDelete}
              className="text-xs text-gray-500 hover:text-red-600 transition-colors"
              aria-label="Delete song"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default SongCard;
